import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  LinearProgress,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DownloadIcon from "@mui/icons-material/Download";

import { IngestDefinitionService } from "@services/ingest/ingestDefinitionService.ts";
import { IngestFileService } from "@services/ingest/ingestFileService.ts";
import type { IngestRun, IngestRunStatus } from "@models/IngestRun.model";
import { appStore } from "@store/appStore";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { rowHoverLiftSx, liftableTableSx } from "@/shared/styles/rowHoverLift";
import { preflightUploadFile, describeStorageFailure, describePresignFailure } from "@services/ingest/uploadHelpers";

const STATUS_COLORS: Record<IngestRunStatus, string> = {
  uploaded: "#845adf",
  upload_failed: "#e6533c",
  validating: "#f5b849",
  validated: "#26bf94",
  validation_failed: "#e6533c",
  running: "#f5b849",
  completed: "#536485",
  failed: "#e6533c",
};

const STATUS_LABELS: Record<IngestRunStatus, string> = {
  uploaded: "Uploaded",
  upload_failed: "Upload failed",
  validating: "Checking file...",
  validated: "Ready to submit",
  validation_failed: "Needs fixing",
  running: "Processing...",
  completed: "Submitted",
  failed: "Failed",
};

interface DefinitionInfo {
  id: number;
  name: string;
  organization_id: number | null;
  population_segment_name: string | null;
}

const fileService = new IngestFileService();

export default function UploadPage() {
  useDocumentTitle("Upload");
  const { configId } = useParams<{ configId: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [definition, setDefinition] = useState<DefinitionInfo | null>(null);
  const [files, setFiles] = useState<IngestRun[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [expandedErrors, setExpandedErrors] = useState<Set<number>>(new Set());
  const [busyRows, setBusyRows] = useState<Set<number>>(new Set());
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [uploadError, setUploadError] = useState<string | null>(null);

  const definitionId = configId ? Number(configId) : null;
  // Raw-file downloads are internal-only (the file carries PII); the server
  // gates the endpoint the same way (MPN-1445).
  const isInternal = appStore.loggedInUser?.permissions?.hasAccess?.("Ingest Configuration") ?? false;

  // A customer loses Discard once the file has been submitted for processing — the
  // server returns 403 on the same statuses (MPN-1820). Disabled rather than hidden,
  // so the reason is on the control the customer goes looking for. A failed run stays
  // discardable: nothing landed, and the customer still needs to clear the row and
  // re-upload without waiting on us.
  const discardLockReason = (status: IngestRunStatus): string | null =>
    !isInternal && status === "completed"
      ? "This file has already been submitted for processing and can no longer be " +
        "removed. Contact your Motimatic representative if it needs to be reversed."
      : null;

  const loadFiles = useCallback(async (orgId: number) => {
    const data = await fileService.listFiles(orgId);
    setFiles(data);
  }, []);

  useEffect(() => {
    if (!definitionId) return;
    (async () => {
      const defs = await new IngestDefinitionService().fetchDefinitions();
      const def = defs.find((d: any) => d.id === definitionId);
      if (!def) return;
      setDefinition({
        id: def.id,
        name: def.name,
        organization_id: def.organization?.id ?? null,
        population_segment_name: def.population_segment_name ?? null,
      });
      if (def.organization?.id) {
        await loadFiles(def.organization.id);
      }
    })();
  }, [definitionId, loadFiles]);

  const uploadDisabled = uploading;

  const runValidate = async (fileId: number) => {
    if (!definitionId) return;
    setBusyRows((prev) => new Set(prev).add(fileId));
    try {
      // The population is the definition's — never a user choice (MPN-1445);
      // the server derives it from the definition.
      const result = await fileService.validateFile(fileId, definitionId);
      if (result.errors?.length || result.warnings?.length) {
        setExpandedErrors((prev) => new Set(prev).add(fileId));
      }
      if (definition?.organization_id) await loadFiles(definition.organization_id);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Validation request failed");
    } finally {
      setBusyRows((prev) => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  const handleUpload = async (file: File) => {
    if (!definition || !definition.organization_id) return;

    const preflightError = preflightUploadFile(file);
    if (preflightError) {
      setUploadError(preflightError);
      return;
    }

    setUploadError(null);
    setUploading(true);
    setUploadProgress(0);
    setUploadFileName(file.name);

    let newRunId: number | undefined;
    try {
      let presign;
      try {
        presign = await fileService.presignUpload(
          definition.organization_id, file.name, file.type || "text/csv", definition.id,
        );
      } catch (err) {
        throw new Error(describePresignFailure(err));
      }
      newRunId = presign.id;

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", presign.url, true);
        xhr.setRequestHeader("Content-Type", file.type || "text/csv");
        // Without a timeout a hung connection leaves the spinner up forever.
        xhr.timeout = 120000;

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(describeStorageFailure(xhr)));
        };
        xhr.onerror = () => reject(new Error("Couldn't reach file storage. Check your connection and try again."));
        xhr.ontimeout = () => reject(new Error("The upload timed out reaching file storage. Please try again."));
        xhr.send(file);
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed";
      // The run row is created at presign, before the bytes transfer. If that transfer
      // failed, mark the run so the list shows the failure instead of a phantom
      // "Uploaded" row for a file that never landed (MPN-1345).
      if (newRunId !== undefined) {
        try {
          await fileService.markUploadFailed(newRunId, message);
        } catch {
          /* best-effort — the banner still surfaces the failure */
        }
        await loadFiles(definition.organization_id);
      }
      setUploadError(message);
      setUploading(false);
      setUploadProgress(0);
      setUploadFileName("");
      return;
    }

    // S3 upload finished -- leave the "Uploading" state before validating so a
    // slow or failing validate reads as row-level activity, not a stuck upload.
    setUploading(false);
    setUploadProgress(0);
    setUploadFileName("");
    await loadFiles(definition.organization_id);
    await runValidate(newRunId!);
  };

  // One file at a time: customers correct-and-reupload a single monthly file.
  // Bulk/historical loads are an ops action on the Config List → Files page.
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (uploadDisabled) return;
    const file = e.dataTransfer.files[0];
    if (file) void handleUpload(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleUpload(file);
    e.target.value = "";
  };

  const handleRun = async (fileId: number) => {
    setRowErrors((prev) => { const next = { ...prev }; delete next[fileId]; return next; });
    setBusyRows((prev) => new Set(prev).add(fileId));
    try {
      await fileService.runFile(fileId);
      if (definition?.organization_id) await loadFiles(definition.organization_id);
    } catch (err: any) {
      const msg = err?.message || "Submit failed";
      setRowErrors((prev) => ({ ...prev, [fileId]: msg }));
    } finally {
      setBusyRows((prev) => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  const handleDiscard = async (fileId: number, status: IngestRunStatus) => {
    const message =
      status === "completed"
        ? "Discard this processed file? It disappears from the list and from rewind/replay. " +
          "Data it loaded stays until you reset the population — the usual order is: reset first, then discard."
        : "Discard this file? It will disappear from the list and won't be processed.";
    if (!window.confirm(message)) return;
    setRowErrors((prev) => { const next = { ...prev }; delete next[fileId]; return next; });
    setBusyRows((prev) => new Set(prev).add(fileId));
    try {
      await fileService.discardFile(fileId);
      if (definition?.organization_id) await loadFiles(definition.organization_id);
    } catch (err: any) {
      setRowErrors((prev) => ({ ...prev, [fileId]: err?.message || "Discard failed" }));
    } finally {
      setBusyRows((prev) => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  const handleDownload = async (file: IngestRun) => {
    try {
      const { url } = await fileService.downloadFile(file.id);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.file_name ?? "";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  const toggleExpanded = (fileId: number) => {
    setExpandedErrors((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      + " " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  };

  const formatRowCount = (count: number | null) => {
    if (count == null) return "—";
    return count.toLocaleString();
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Box
          component="a"
          onClick={() => navigate("/uploads")}
          sx={{
            color: "#845adf",
            cursor: "pointer",
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            fontSize: 14,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          <ArrowBackIosNewIcon sx={{ fontSize: 12, mr: 0.5 }} />
          All uploads
        </Box>
        <Typography sx={{ color: "#8c9097", mx: 1 }}>&middot;</Typography>
        <Typography variant="h6" fontWeight={600}>{definition?.name ?? "..."}</Typography>
      </Box>

      {/* Help / Instructions */}
      <Box sx={{ mb: 2, p: 2, bgcolor: "#fafafa", border: "1px solid #f3f3f3", borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 0.75 }}>How this works</Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, color: "#536485", fontSize: 13, lineHeight: 1.8 }}>
          <li>Upload a CSV file (not Excel). Your file will be checked automatically.</li>
          <li>If the check passes, click <strong>Submit</strong> to process the file.</li>
          <li>If the check finds issues, fix them in your file and upload again.</li>
          <li>Questions? Contact your Motimatic team.</li>
        </Box>
      </Box>

      {uploadError && (
        <Alert severity="error" onClose={() => setUploadError(null)} sx={{ mb: 2 }}>
          {uploadError}
        </Alert>
      )}

      {/* Drop Zone */}
      <Box
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !uploadDisabled && fileInputRef.current?.click()}
        sx={{
          border: uploading
            ? "2px solid #845adf"
            : dragOver
              ? "2px solid #845adf"
              : "2px dashed #e9edf6",
          borderRadius: 2,
          p: 3,
          textAlign: "center",
          bgcolor: uploading || dragOver ? "rgba(132,90,223,0.03)" : "#f9fafb",
          mb: 2,
          cursor: uploadDisabled ? "default" : "pointer",
          opacity: uploadDisabled && !uploading ? 0.7 : 1,
          transition: "border-color 0.2s, background-color 0.2s",
        }}
      >
        {uploading ? (
          <>
            <Typography fontWeight={500} mb={1}>Uploading {uploadFileName}...</Typography>
            <Box sx={{ maxWidth: 300, mx: "auto" }}>
              <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 6, borderRadius: 1 }} />
            </Box>
            <Typography sx={{ color: "#8c9097", fontSize: 13, mt: 0.5 }}>{uploadProgress}%</Typography>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: 22, opacity: 0.4, mb: 0.5 }}>&#128196;</Typography>
            <Typography fontWeight={500} sx={{ color: "#536485" }}>Drop your data file here or click to choose</Typography>
            <Typography sx={{ color: "#8c9097", fontSize: 13 }}>CSV files only</Typography>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />
      </Box>

      {/* History Table */}
      <TableContainer sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #f3f3f3" }}>
        <Table size="small" sx={liftableTableSx}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f9fafb" }}>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }}>File</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }}>Rows</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }}>Uploaded</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file) => {
              const isBusy = busyRows.has(file.id);
              const discardLock = discardLockReason(file.status);
              // Status detail is persisted by the backend (validate + run), so it
              // survives navigating away and back and is present for successful
              // runs too, not just failures (MPN-1345).
              const sd = {
                message: file.status_message ?? "",
                errors: file.status_errors ?? [],
                warnings: file.status_warnings ?? [],
              };
              const isExpanded = expandedErrors.has(file.id);
              const hasDetail = !!(sd.message || sd.errors.length || sd.warnings.length);

              return (
                <React.Fragment key={file.id}>
                  <TableRow
                    onClick={hasDetail ? () => toggleExpanded(file.id) : undefined}
                    sx={[hasDetail && rowHoverLiftSx, { cursor: hasDetail ? "pointer" : "default" }]}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        <span>{file.file_name ?? "—"}</span>
                        {/* Downloads are internal-only (raw file carries PII) —
                            the server gates the endpoint the same way (MPN-1445). */}
                        {isInternal && file.s3_uri && (
                          <Box
                            component="a"
                            onClick={(e) => { e.stopPropagation(); handleDownload(file); }}
                            title="Download file (internal)"
                            sx={{
                              cursor: "pointer",
                              color: "#b5bac1",
                              display: "inline-flex",
                              "&:hover": { color: "#845adf" },
                            }}
                          >
                            <DownloadIcon sx={{ fontSize: 14 }} />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {/* A file waiting on another one sits at 'validated' like any
                            pre-load file. Saying so is the whole point here: the person
                            who uploaded it is the one wondering why nothing happened
                            (MPN-1564). */}
                        <span
                          style={{
                            color: file.waiting_on ? "#f5b849" : STATUS_COLORS[file.status],
                            fontSize: 13,
                            fontWeight: 500,
                          }}
                          title={
                            file.waiting_on
                              ? `This file loads automatically once "${file.waiting_on}" has been processed.`
                              : undefined
                          }
                        >
                          {file.waiting_on ? `Waiting on ${file.waiting_on}` : STATUS_LABELS[file.status]}
                        </span>
                        {hasDetail && (
                          <Box
                            component="a"
                            onClick={(e) => { e.stopPropagation(); toggleExpanded(file.id); }}
                            sx={{ color: "#8c9097", fontSize: 12, cursor: "pointer", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                          >
                            {isExpanded ? "Hide details" : "Details"}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{formatRowCount(file.row_count)}</TableCell>
                    <TableCell sx={{ color: "#8c9097" }}>{formatDate(file.create_date)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                        <Box
                          component="button"
                          disabled={isBusy || file.status !== "validated"}
                          onClick={(e) => { e.stopPropagation(); handleRun(file.id); }}
                          sx={{
                            bgcolor: "#26bf94",
                            color: "#fff",
                            border: 0,
                            borderRadius: 1,
                            px: 1.5,
                            py: 0.5,
                            fontSize: 12,
                            fontWeight: 500,
                            cursor: isBusy || file.status !== "validated" ? "not-allowed" : "pointer",
                            opacity: isBusy || file.status !== "validated" ? 0.5 : 1,
                            minWidth: 70,
                            textTransform: "none",
                          }}
                        >
                          Submit
                        </Box>
                        {file.status !== "running" && file.status !== "validating" && (
                          <Box
                            component="button"
                            disabled={isBusy || !!discardLock}
                            title={
                              discardLock ??
                              (isInternal
                                ? "Remove this file from the list. For processed files: reset the population first (discard clears the row, not the data it loaded)."
                                : "Remove this file from the list. It won't be processed.")
                            }
                            onClick={(e) => { e.stopPropagation(); handleDiscard(file.id, file.status); }}
                            sx={{
                              bgcolor: "transparent",
                              color: "#e6533c",
                              border: "1px solid #e6533c",
                              borderRadius: 1,
                              px: 1.5,
                              py: 0.5,
                              fontSize: 12,
                              fontWeight: 500,
                              cursor: isBusy || discardLock ? "not-allowed" : "pointer",
                              opacity: isBusy || discardLock ? 0.5 : 1,
                              textTransform: "none",
                            }}
                          >
                            Discard
                          </Box>
                        )}
                      </Box>
                      {rowErrors[file.id] && (
                        <Typography sx={{ color: "#e6533c", fontSize: 12, mt: 0.5 }}>
                          {rowErrors[file.id]}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  {hasDetail && isExpanded && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{
                          p: "4px 14px 14px 14px",
                          bgcolor: sd.errors.length ? "rgba(230,83,60,0.02)" : "#fafafb",
                        }}
                      >
                        <Box sx={{ bgcolor: "#fff", border: "1px solid #f3f3f3", borderRadius: 1.5, p: 2 }}>
                          {sd.message && (
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: 13,
                                color: STATUS_COLORS[file.status],
                                mb: sd.errors.length || sd.warnings.length ? 1.25 : 0,
                              }}
                            >
                              {sd.message}
                            </Typography>
                          )}
                          {sd.errors.length > 0 && (
                            <>
                              <Typography sx={{ fontWeight: 600, color: "#e6533c", fontSize: 13, mb: 0.5 }}>Issues to fix</Typography>
                              <Box component="ul" sx={{ m: 0, pl: 2.5, color: "#536485", fontSize: 13, lineHeight: 1.8, mb: sd.warnings.length ? 1.5 : 0 }}>
                                {sd.errors.map((err, i) => (
                                  <li key={i}>{err}</li>
                                ))}
                              </Box>
                            </>
                          )}
                          {sd.warnings.length > 0 && (
                            <>
                              <Typography sx={{ fontWeight: 600, color: "#f5b849", fontSize: 13, mb: 0.5 }}>Warnings</Typography>
                              <Box component="ul" sx={{ m: 0, pl: 2.5, color: "#536485", fontSize: 13, lineHeight: 1.8 }}>
                                {sd.warnings.map((warn, i) => (
                                  <li key={i}>{warn}</li>
                                ))}
                              </Box>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
        {files.length === 0 && !uploading && (
          <Box sx={{ p: 4, textAlign: "center", color: "#8c9097" }}>
            <Typography>No files uploaded yet. Drop a CSV above to get started.</Typography>
          </Box>
        )}
      </TableContainer>
    </Box>
  );
}
